// Centralized axios instance. All API calls go through this.
import axios from 'axios';
import env from '../config/env.js';
import { generateCorrelationId } from '../utils/correlationId.js';

const apiClient = axios.create({
  baseURL: env.API_BASE_URL,
  timeout: 20000,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

const TOKEN_KEY = 'itec_auth_token';

export const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
export const setStoredToken = (token) => localStorage.setItem(TOKEN_KEY, token);
export const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

apiClient.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Tag every request with a fresh correlation ID for end-to-end tracing.
  const correlationId = generateCorrelationId();
  config.headers['X-Correlation-Id'] = correlationId;
  config.metadata = { ...(config.metadata || {}), correlationId };
  return config;
});

apiClient.interceptors.response.use(
  (response) => (response.config?.returnFullResponse ? response : response.data),
  (error) => {
    const status = error.response?.status;
    // The server only sends messages written for users (see the backend's
    // errorHandler), so its message is safe to surface. When the request never
    // reached the server, axios supplies its own text — "Network Error",
    // "timeout of 20000ms exceeded" — which describes the transport rather than
    // anything the user can act on, so it is replaced here.
    const message =
      error.response?.data?.message ||
      (error.response
        ? 'Something went wrong. Please try again.'
        : 'We could not reach the server. Please check your connection and try again.');

    // Prefer the ID the backend echoed back; fall back to the one we sent.
    const correlationId =
      error.response?.headers?.['x-correlation-id'] ||
      error.response?.data?.correlationId ||
      error.config?.metadata?.correlationId ||
      null;

    if (status === 401) {
      clearStoredToken();
      if (typeof window !== 'undefined') {
        const path = window.location.pathname;
        // Staff areas bounce to the staff login, not the customer one.
        const isStaffArea =
          path.startsWith('/admin') || path.startsWith('/marketer');
        const target = isStaffArea ? '/admin/login' : '/login';
        if (path !== target) {
          window.location.href = target;
        }
      }
    }

    return Promise.reject({
      status,
      message,
      correlationId,
      data: error.response?.data,
    });
  }
);

export default apiClient;
